'use client'

import { useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ChevronLeft, ChevronRight, Play } from 'lucide-react'

interface Workshop {
  title: string
  date: string
  category: string
  description: string
  image: string
  /** Optional preview clip. When set, the card's play button plays it inline
   *  instead of navigating to the workshops hub. */
  video?: string
}

const WORKSHOP_HREF = '/courses/live-ai-workshops'

const workshops: Workshop[] = [
  {
    title: 'Jev: Decisions Instead of Text, at a Fraction of LLM Cost',
    date: 'Sep 20',
    category: 'Decision Models',
    description:
      'A model that returns a probability for every option instead of generating text. Jason walks through the three output types, the confidence threshold that routes to a human, the 32K limit, and the signup fraud scan, buying signal filter and launch filter he runs with it in production for a fraction of an LLM call.',
    image: '/thumbnails/workshops/jev-decision-model.png',
  },
  {
    title: 'Realistic AI UGC: Characters That Don\'t Look AI',
    date: 'Sep 13',
    category: 'AI Video',
    description:
      'Video of a real person is easy now. A brand new person who looks real is the hard part, so this session builds one: a locked JSON character prompt that fights the image model\'s defaults, the model that draws the most believable face, and Seedance 2.5 with a real audio reference.',
    image: '/thumbnails/workshops/ai-ugc-characters.png',
    video: '/videos/ai-ugc-characters-preview.mp4',
  },
  {
    title: 'Making High Quality Launch Videos with HyperFrames',
    date: 'Sep 6',
    category: 'AI Video',
    description:
      'Jason builds a 78-second launch video with HyperFrames from scratch. Why HTML gives an agent a timeline it can check frame by frame, and why the first few compositions need hand direction before the rest can copy them.',
    image: '/thumbnails/workshops/hyperframes-launch-video.png',
    video: '/videos/hyperframes-launch-video-preview.mp4',
  },
  {
    title: 'Graph Engineering Without a Framework',
    date: 'Aug 2',
    category: 'Graph Engineering',
    description:
      'One term covering three different problems, and the two ways to actually enforce a graph - control flow written as code, or an SOP written into a skill the model runs. Why skills plus a few scripts beat a framework for most workflows, and how to cut a job into agent nodes.',
    image: '/thumbnails/workshops/graph-engineering.png',
    video: '/videos/graph-engineering-preview.mp4',
  },
  {
    title: 'Loop Engineering Best Practices',
    date: 'Jun 14',
    category: 'Agent Loops',
    description:
      'Replacing manual prompting with self-running agent loops across support, SEO, growth, and ads - Claude Code on local machines with cron triggers. Artifacts, loop contracts, a global logs.md, and the open vs closed loop distinction that makes fixes compound.',
    image: '/thumbnails/workshops/loop-engineering.png',
  },
  {
    title: 'Harness Engineering',
    date: 'Jun 7',
    category: 'Agent Infra',
    description:
      'Make your repo Legible, Executable, and Verifiable for agents. Sub-100-line CLAUDE.md as an index, custom lint rules as constraints, one-script dev servers with configurable ports, and Playwright PR verification loops.',
    image: '/thumbnails/workshops/harness-engineering.png',
  },
  {
    title: 'AI Wiki Toolkit',
    date: 'May 17',
    category: 'Agent Memory',
    description:
      'Eliminating agent memory loss in development. A /memories, /drafts, /metrics folder structure, system vs repo-level separation, prompt injection into agents.md/claude.md, and a routing system to pick the right files from 100+ accumulated memories.',
    image: '/thumbnails/workshops/ai-wiki-toolkit.png',
  },
  {
    title: 'OpenAI Symphony + Automated Testing',
    date: 'May 3',
    category: 'Orchestration',
    description:
      "OpenAI's Symphony open-source scheduler for coding agents, Linear integration via workflow.md, making codebases harness-ready with end-to-end testing, Playwright CLI video evidence, isolated per-ticket workspaces, and token/parallel-execution tradeoffs.",
    image: '/thumbnails/workshops/openai-symphony.png',
  },
  {
    title: 'Open Organizational AI with PKA',
    date: 'Apr 26',
    category: 'AI DevOps',
    description:
      "Simon Wallace-Jones's production system coordinating work across 36 codebases via a Personal Knowledge Assistant - 17+ dev tasks per week, autonomously, with full visibility and control.",
    image: '/thumbnails/workshops/open-org-ai.png',
  },
  {
    title: 'OpenClaw Production Guide',
    date: 'Mar 29',
    category: 'AI Product',
    description:
      'Production-ready OpenClaw for real business workflows: local vs VPS deployment, Slack multi-user teams, 3 levels of memory management, web access strategies, and support/marketing/sales automation patterns.',
    image: '/thumbnails/workshops/openclaw-production.png',
  },
]

function WorkshopCard({ workshop }: { workshop: Workshop }) {
  const [playing, setPlaying] = useState(false)

  const thumbnail = (
    <>
      <Image
        src={workshop.image}
        alt={`${workshop.title} workshop`}
        fill
        className="object-cover transition-transform duration-300 group-hover:scale-105"
        sizes="320px"
      />
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors" />
      <div
        className={`absolute inset-0 flex items-center justify-center transition-opacity ${
          workshop.video ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
      >
        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
          <Play className="h-5 w-5 text-black ml-0.5" fill="black" />
        </div>
      </div>
      <span className="absolute top-2.5 left-2.5 bg-black/80 text-white text-[11px] px-2 py-0.5 rounded-full font-medium">
        {workshop.date}
      </span>
    </>
  )

  const body = (
    <div className="p-4">
      <p className="text-[11px] uppercase tracking-widest text-white/40 font-medium mb-1.5">
        {workshop.category}
      </p>
      <h3 className="font-semibold text-base text-white leading-snug mb-2 line-clamp-2">
        {workshop.title}
      </h3>
      <p className="text-gray-400 text-[13px] leading-relaxed line-clamp-3">
        {workshop.description}
      </p>
    </div>
  )

  // Cards without a clip keep the original behaviour: the whole card is one link.
  if (!workshop.video) {
    return (
      <Link
        href={WORKSHOP_HREF}
        className="group flex-shrink-0 w-[280px] sm:w-[320px]"
        aria-label={`${workshop.title} - watch in Weekly Live AI Workshops`}
      >
        <article className="h-full bg-[#111111] border border-gray-800 rounded-2xl overflow-hidden transition-all duration-300 hover:bg-[#141414] hover:border-gray-700">
          <div className="relative aspect-video overflow-hidden">{thumbnail}</div>
          {body}
        </article>
      </Link>
    )
  }

  return (
    <article className="group flex-shrink-0 w-[280px] sm:w-[320px] h-full bg-[#111111] border border-gray-800 rounded-2xl overflow-hidden transition-all duration-300 hover:bg-[#141414] hover:border-gray-700">
      {playing ? (
        <div className="relative aspect-video overflow-hidden bg-black">
          <video
            // Start from the click that mounted this, rather than the autoplay
            // attribute: a clip with sound is blocked by autoplay policy, but a
            // play() call inside the user gesture is allowed.
            ref={(el) => {
              if (el) void el.play().catch(() => {})
            }}
            // Only one preview plays at a time: starting this one pauses any
            // sibling card the visitor left running.
            onPlay={(e) => {
              document.querySelectorAll('video').forEach((v) => {
                if (v !== e.currentTarget) v.pause()
              })
            }}
            onEnded={() => setPlaying(false)}
            src={workshop.video}
            controls
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="relative aspect-video overflow-hidden block w-full"
          aria-label={`Play the ${workshop.title} preview`}
        >
          {thumbnail}
        </button>
      )}
      <Link
        href={WORKSHOP_HREF}
        aria-label={`${workshop.title} - watch in Weekly Live AI Workshops`}
      >
        {body}
      </Link>
    </article>
  )
}

export default function WorkshopCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null)

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return
    const amount = 340
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    })
  }

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto scrollbar-hide pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {workshops.map((workshop) => (
          <WorkshopCard key={workshop.title} workshop={workshop} />
        ))}
      </div>
      <button
        onClick={() => scroll('left')}
        className="absolute left-0 top-[28%] -translate-y-1/2 -translate-x-3 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center transition-colors backdrop-blur-sm hidden lg:flex"
        aria-label="Scroll left"
      >
        <ChevronLeft className="h-4 w-4 text-white" />
      </button>
      <button
        onClick={() => scroll('right')}
        className="absolute right-0 top-[28%] -translate-y-1/2 translate-x-3 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center transition-colors backdrop-blur-sm hidden lg:flex"
        aria-label="Scroll right"
      >
        <ChevronRight className="h-4 w-4 text-white" />
      </button>
    </div>
  )
}
